/**
 * Shared Styles
 *
 * Common CSS styles used across Lit components.
 * Extracted and converted from eaidev.css
 */

import { css } from 'lit';

/**
 * Color variables
 */
export const colors = css`
    :host {
        --primary-color: #007bff;
        --primary-hover: #0056b3;
        --secondary-color: #6c757d;
        --success-color: #28a745;
        --danger-color: #dc3545;
        --warning-color: #ffc107;

        --text-primary: #212529;
        --text-secondary: #6c757d;
        --text-light: #ffffff;

        --bg-light: #f8f9fa;
        --bg-white: #ffffff;
        --bg-dark: #343a40;
        --bg-user-message: #111111;
        --bg-bot-message: #f6f6f6;

        --border-color: #dee2e6;
        --border-radius: 8px;
        --border-radius-lg: 12px;

        --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.05);
        --shadow-md: 0 4px 6px rgba(0, 0, 0, 0.1);
        --shadow-lg: 0 10px 15px rgba(0, 0, 0, 0.1);

        --font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        --font-size-sm: 12px;
        --font-size-base: 14px;
        --font-size-lg: 16px;

        --spacing-xs: 4px;
        --spacing-sm: 8px;
        --spacing-md: 16px;
        --spacing-lg: 24px;
        --spacing-xl: 32px;

        --transition-fast: 0.15s ease;
        --transition-normal: 0.3s ease;
    }
`;

/**
 * Typography styles
 */
export const typography = css`
    .text-sm { font-size: var(--font-size-sm); }
    .text-base { font-size: var(--font-size-base); }
    .text-lg { font-size: var(--font-size-lg); }

    .font-normal { font-weight: 400; }
    .font-medium { font-weight: 500; }
    .font-semibold { font-weight: 600; }
    .font-bold { font-weight: 700; }

    .text-primary { color: var(--text-primary); }
    .text-secondary { color: var(--text-secondary); }
    .text-light { color: var(--text-light); }
`;

/**
 * Button styles
 */
export const buttonStyles = css`
    .btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 8px 16px;
        font-size: var(--font-size-base);
        font-weight: 500;
        line-height: 1.5;
        text-align: center;
        text-decoration: none;
        border: 1px solid transparent;
        border-radius: var(--border-radius);
        cursor: pointer;
        transition: all var(--transition-fast);
        user-select: none;
    }

    .btn:disabled {
        opacity: 0.65;
        cursor: not-allowed;
    }

    .btn-primary {
        color: var(--text-light);
        background-color: var(--primary-color);
        border-color: var(--primary-color);
    }

    .btn-primary:hover:not(:disabled) {
        background-color: var(--primary-hover);
        border-color: var(--primary-hover);
    }

    .btn-secondary {
        color: var(--text-light);
        background-color: var(--secondary-color);
        border-color: var(--secondary-color);
    }

    .btn-outline {
        color: var(--primary-color);
        background-color: transparent;
        border-color: var(--primary-color);
    }

    .btn-outline:hover:not(:disabled) {
        color: var(--text-light);
        background-color: var(--primary-color);
    }

    .btn-icon {
        padding: 8px;
        border-radius: 50%;
    }

    .btn-sm {
        padding: 4px 12px;
        font-size: var(--font-size-sm);
    }

    .btn-lg {
        padding: 12px 24px;
        font-size: var(--font-size-lg);
    }
`;

/**
 * Input styles
 */
export const inputStyles = css`
    .input {
        display: block;
        width: 100%;
        padding: 10px 14px;
        font-size: var(--font-size-base);
        font-family: var(--font-family);
        line-height: 1.5;
        color: var(--text-primary);
        background-color: var(--bg-white);
        border: 1px solid var(--border-color);
        border-radius: var(--border-radius);
        transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
    }

    .input:focus {
        outline: none;
        border-color: var(--primary-color);
        box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.15);
    }

    .input::placeholder {
        color: var(--text-secondary);
    }

    .input:disabled {
        background-color: var(--bg-light);
        cursor: not-allowed;
    }

    .textarea {
        min-height: 80px;
        resize: vertical;
    }
`;

/**
 * Message bubble styles
 */
export const messageBubbleStyles = css`
    .message {
        display: flex;
        margin-bottom: var(--spacing-md);
        animation: fadeIn 0.3s ease;
    }

    @keyframes fadeIn {
        from {
            opacity: 0;
            transform: translateY(10px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    .message--user {
        justify-content: flex-end;
        margin-bottom: 10px;
    }

    .message--bot {
        justify-content: flex-start;
        margin-bottom: 0;
    }

    .message__avatar {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        margin-right: var(--spacing-sm);
        flex-shrink: 0;
    }

    .message__bubble {
        padding: 12px 16px;
        border-radius: var(--border-radius-lg);
        font-size: var(--font-size-base);
        line-height: 1.4;
        word-wrap: break-word;
    }

    .message--user .message__bubble {
        background-color: var(--bg-user-message);
        color: var(--text-light);
        border-bottom-right-radius: 4px;
    }

    .message--bot .message__bubble {
        background-color: var(--bg-bot-message);
        color: var(--text-primary);
        border-top-left-radius: 4px;
    }

    .message__time {
        font-size: var(--font-size-sm);
        color: var(--text-secondary);
        margin-top: 4px;
    }

    .message--user .message__time {
        text-align: right;
    }
`;

/**
 * Card styles
 */
export const cardStyles = css`
    .card {
        background-color: var(--bg-white);
        border: 1px solid var(--border-color);
        border-radius: var(--border-radius-lg);
        box-shadow: var(--shadow-sm);
        overflow: hidden;
    }

    .card__header {
        padding: var(--spacing-md);
        border-bottom: 1px solid var(--border-color);
        background-color: var(--bg-light);
    }

    .card__body {
        padding: var(--spacing-md);
    }

    .card__footer {
        padding: var(--spacing-md);
        border-top: 1px solid var(--border-color);
        background-color: var(--bg-light);
    }
`;

/**
 * Scrollbar styles
 */
export const scrollbarStyles = css`
    ::-webkit-scrollbar {
        width: 6px;
    }

    ::-webkit-scrollbar-track {
        background: transparent;
    }

    ::-webkit-scrollbar-thumb {
        background: var(--border-color);
        border-radius: 3px;
    }

    ::-webkit-scrollbar-thumb:hover {
        background: var(--text-secondary);
    }
`;

/**
 * Utility classes
 */
export const utilities = css`
    .flex { display: flex; }
    .flex-col { flex-direction: column; }
    .items-center { align-items: center; }
    .justify-center { justify-content: center; }
    .justify-between { justify-content: space-between; }
    .gap-sm { gap: var(--spacing-sm); }
    .gap-md { gap: var(--spacing-md); }

    .w-full { width: 100%; }
    .h-full { height: 100%; }

    .p-sm { padding: var(--spacing-sm); }
    .p-md { padding: var(--spacing-md); }
    .p-lg { padding: var(--spacing-lg); }

    .m-0 { margin: 0; }
    .mb-sm { margin-bottom: var(--spacing-sm); }
    .mb-md { margin-bottom: var(--spacing-md); }

    .rounded { border-radius: var(--border-radius); }
    .rounded-lg { border-radius: var(--border-radius-lg); }
    .rounded-full { border-radius: 9999px; }

    .shadow-sm { box-shadow: var(--shadow-sm); }
    .shadow-md { box-shadow: var(--shadow-md); }
    .shadow-lg { box-shadow: var(--shadow-lg); }

    .overflow-hidden { overflow: hidden; }
    .overflow-auto { overflow: auto; }

    .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
    }
`;

/**
 * Combined base styles for components
 */
export const baseStyles = css`
    ${colors}
    ${typography}
    ${buttonStyles}
    ${inputStyles}
    ${scrollbarStyles}
    ${utilities}

    * {
        box-sizing: border-box;
    }
`;
